import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SocketState } from './matchSlice';
import { RootState } from '../store';

interface ChatMessage {
  id: string;
  user_id: string;
  user_roles: number;
  username: string;
  image: string | null;
  time: Date;
  message: string;
  isDeleted: boolean;
  match_id?: string | null;
  attachments?: string[];
}

interface MatchGameScreenshot {
  id: number;
  user_id: number;
  chatroomId: number;
  message: string;
}
interface Chatroom {
  id: number;
  messages: ChatMessage[];
  MatchGameScreenshot: MatchGameScreenshot[];
  ChatroomUsers: Array<{
    userId: string;
    isArchived: boolean;
    lastReadDate?: string | null;
  }>;
}

interface MatchChat {
  id: string;
  messages: ChatMessage[];
}

interface Announcement {
  Recipients: any[];
  id: string;
  inbox_announcement_type: string;
  match_id: string;
  send_time: string;
  message: string;
  show_as_popup: boolean;
  user_id: string;
}

interface MyState {
  socket: SocketState;
  announcements: Announcement[] | undefined;
  chatrooms?: Chatroom[] | undefined;
  matchChats: MatchChat[] | undefined;
  activeChatroomId: string | null;
  users: Array<any>;
}
const initialState: MyState = {
  socket: 'disconnected',
  announcements: undefined,
  chatrooms: undefined,
  matchChats: undefined,
  activeChatroomId: null,
  users: [],
};

const inboxSlice = createSlice({
  name: 'inbox',
  initialState,
  reducers: {
    setActiveChatroomId: (state, action: PayloadAction<string | null>) => {
      state.activeChatroomId = action.payload;
    },
    setChatrooms: (state, action: PayloadAction<Chatroom[] | undefined>) => {
      state.chatrooms = action.payload;
    },
    updateChatroomSocket: (state, { payload }: { payload: SocketState }) => {
      state.socket = payload;
    },
    initChatroomMessages: (
      state,
      action: PayloadAction<{ chatroomId: string; messages: ChatMessage[] }>,
    ) => {
      const { chatroomId, messages } = action.payload;
      const chatroom = state.chatrooms?.find((c) => c.id === +chatroomId);
      if (chatroom) {
        chatroom.messages = messages;
      }
    },
    pushChatroomMessages: (
      state,
      action: PayloadAction<{ chatroomId: string; messages: ChatMessage[] }>,
    ) => {
      const { chatroomId, messages } = action.payload;
      const chatroom = state.chatrooms?.find((c) => c.id === +chatroomId);
      if (chatroom) {
        chatroom.messages.push(...messages);
      }
    },
    addChatroom: (state, action: PayloadAction<Chatroom>) => {
      state.chatrooms?.unshift(action.payload);
    },
    addChatroomMessage: (
      state,
      action: PayloadAction<{ chatroomId: string; message: ChatMessage }>,
    ) => {
      const { chatroomId, message } = action.payload;
      const chatroom = state.chatrooms?.find((c) => c.id === +chatroomId);
      if (chatroom) {
        chatroom.messages.push(message);
      }
    },
    addOrUpdateChatroom: (state, action: PayloadAction<Chatroom>) => {
      if (!state.chatrooms) {
        state.chatrooms = [action.payload];
      } else {
        const index = state.chatrooms.findIndex((c) => c.id === action.payload.id);
        if (index >= 0) {
          state.chatrooms[index] = action.payload;
        } else {
          state.chatrooms.push(action.payload);
        }
      }
    },
    unarchiveChatroom: (state, action: PayloadAction<{ chatroomId: number; userId: number }>) => {
      const { chatroomId, userId } = action.payload;
      const chatroom = state.chatrooms?.find((c) => c.id === chatroomId);
      if (chatroom) {
        const chatroomUser = chatroom.ChatroomUsers.find(
          (user) => user.userId == userId.toString(),
        );
        if (chatroomUser) {
          chatroomUser.isArchived = false;
        }
      }
    },
    addChatroomDispute: (state, action: PayloadAction<MatchGameScreenshot>) => {
      const chatroom = state.chatrooms?.find((c) => c.id === action.payload.chatroomId);
      chatroom?.MatchGameScreenshot.push(action.payload);
    },
    updateChatroomDispute: (state, action: PayloadAction<MatchGameScreenshot>) => {
      const chatroom = state.chatrooms?.find((c) => c.id === action.payload.chatroomId);
      const dispute = chatroom?.MatchGameScreenshot.find((d) => d.id === action.payload.id);
      if (dispute) {
        Object.assign(dispute, action.payload);
      }
    },
    clearChatroom: (state) => {
      state = initialState;
    },
    setMatchChat: (state, action: PayloadAction<MatchChat[] | undefined>) => {
      state.matchChats = action.payload;
    },
    addMatchChat: (state, action: PayloadAction<ChatMessage>) => {
      if (!state.matchChats) {
        state.matchChats = [];
      }
      const matchChat = state.matchChats.find((c) => c.id === action.payload.match_id);
      if (matchChat) {
        matchChat.messages.push(action.payload);
      }
    },
    addImagesToChatroomChat: (
      state,
      action: PayloadAction<{ messageId: number; attachments: string[] }>,
    ) => {
      const { messageId, attachments } = action.payload;
      const chatroom = state.chatrooms?.find((c) => c.messages.some((m) => +m.id === messageId));
      if (chatroom) {
        const message = chatroom.messages.find((m) => +m.id === messageId);
        if (message) {
          message.attachments = attachments;
        }
      }
    },
    setAnnouncements: (state, action: PayloadAction<Announcement[] | undefined>) => {
      state.announcements = action.payload;
    },
    NewAnnouncement: (state, action: PayloadAction<Announcement>) => {
      if (!state.announcements) {
        state.announcements = [];
      }
      state.announcements.push(action.payload);
    },
  },
});

const { reducer, actions } = inboxSlice;

export const {
  setActiveChatroomId,
  setChatrooms,
  updateChatroomSocket,
  initChatroomMessages,
  pushChatroomMessages,
  addChatroomMessage,
  addChatroom,
  addOrUpdateChatroom,
  unarchiveChatroom,
  addChatroomDispute,
  updateChatroomDispute,
  clearChatroom,
  setMatchChat,
  addMatchChat,
  addImagesToChatroomChat,
  setAnnouncements,
  NewAnnouncement,
} = actions;
const selectSelf = (state: RootState) => state.inbox;

export const makeSelectChatroomMessages = (chatroomId: string) =>
  createSelector(selectSelf, (state) => {
    const chatroom = state.chatrooms?.find((c) => c.id === +chatroomId);
    return chatroom ? [...chatroom.messages] : [];
  });
export const selectActiveChatroom = (state: RootState) => {
  const { chatrooms, activeChatroomId } = state.inbox;
  return chatrooms?.find((c) => c.id === +activeChatroomId!) || null;
};
export const selectChatrooms = (state: RootState) => state.inbox.chatrooms;
export const selectActiveChatroomId = (state: RootState) => state.inbox.activeChatroomId;
export const selectMatchChats = (state: RootState) => state.inbox.matchChats;
export const selectAnnouncements = (state: RootState) => state.inbox.announcements;
export default inboxSlice.reducer;
